@if (isLoading()) {
  <div class="loading-container">
    <app-loader></app-loader>
  </div>
} @else {
  <button
    #printButton
    class="hide"
    [useExistingCss]="true"
    [printStyle]="printStyle"
    [printSectionId]="printSectionId"
    ngxPrint
  >
    -
  </button>

  <div [id]="printSectionId" class="report-container">
    <div class="title-section">
      <div class="title">
        <h1 i18n="@@riskassessment.report.title" class="report-title">Risk assessment report</h1>
        <div class="organization-name">
          {{ activeOrganisation.name }}
        </div>

        <div class="record-name">
          {{ recordNameOrId() }}
        </div>
      </div>

      <div class="print-button">
        <app-button (pressed)="printButton.click()">
          <mat-icon icon-left>print</mat-icon>
          <ng-container i18n="@@report.print">Print</ng-container>
        </app-button>
      </div>
    </div>

    <div class="content-grid">
      <div class="record-details">
        <h2>{{ translations.recordDetails }}</h2>
        @if (recordDetails().organisation) {
          <ng-container
            *ngTemplateOutlet="
              organisationTemplate;
              context: { data: recordDetails().organisation }
            "
          ></ng-container>
        }
        @if (recordDetails().location) {
          <ng-container
            *ngTemplateOutlet="locationTemplate; context: { data: recordDetails().location }"
          ></ng-container>
        }
        @if (recordDetails().product) {
          <ng-container
            *ngTemplateOutlet="
              genericDetailsTemplate;
              context: { data: recordDetails().product, label: translations.product }
            "
          ></ng-container>
        }
        @if (recordDetails().material) {
          <ng-container
            *ngTemplateOutlet="
              genericDetailsTemplate;
              context: { data: recordDetails().material, label: translations.material }
            "
          ></ng-container>
        }
        @if (recordDetails().country) {
          <ng-container
            *ngTemplateOutlet="countryTemplate; context: { data: recordDetails().country }"
          ></ng-container>
        }

        @if (recordDetails().delivery) {
          <ng-container
            *ngTemplateOutlet="deliveryTemplate; context: { data: recordDetails().delivery }"
          ></ng-container>
        }
      </div>

      <div class="risk-assessment-details">
        <h2 i18n="@@report.riskassessmentdetails">Risk assessment details</h2>
        <div class="details-grid">
          <div class="detail-row">
            <div class="label">{{ translations.completedDate }}</div>
            <div class="value">{{ report().completedDate | appDate: dateFormat() }}</div>
          </div>

          <div class="detail-row">
            <div class="label">{{ translations.validUntil }}</div>
            <div class="value">{{ report().validUntilDate | appDate: dateFormat() }}</div>
          </div>

          <div class="detail-row">
            <div class="label">{{ translations.note }}</div>
            <div class="value">
              <div [innerHTML]="note()"></div>
            </div>
          </div>

          <div class="detail-row">
            <div class="label">{{ translations.calculatedRisk }}</div>
            <div class="value">
              <app-basic-tags [tags]="[calculatedRisk()]" />
            </div>
          </div>

          <div class="detail-row">
            <div class="label">{{ translations.residualRisk }}</div>
            <div class="value">
              @if (residualRisk()) {
                <app-basic-tags [tags]="[residualRisk()]" />
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="risks-section">
      @for (indicatorGroup of indicatorGroups(); track indicatorGroup.id) {
        <h2>{{ indicatorGroup.name }}</h2>

        @for (indicator of indicatorGroup.indicators; track indicator.id) {
          <app-risk-assessment-indicator-report-print-view
            [indicator]="indicator"
            [mitigations]="reportIndicatorMitigationsMapService.map().get(indicator.id)"
          />
        }
      }
    </div>
  </div>
}

<ng-template #locationTemplate let-data="data">
  <div class="details-grid">
    <div class="detail-row">
      <div class="label">{{ translations.location }}</div>
      <div class="value">{{ data.name | emptyValue }}</div>
    </div>
    <div class="detail-row">
      <div class="label">{{ translations.organisation }}</div>
      <div class="value">{{ data.organisationName | emptyValue }}</div>
    </div>
    <div class="detail-row">
      <div class="label">{{ translations.types }}</div>
      <div class="value">
        <app-tag-group [tags]="getTags(data.types)" />
      </div>
    </div>
    <div class="detail-row">
      <div class="label">{{ translations.address }}</div>
      <div class="value">{{ data.address.street | emptyValue }}</div>
    </div>
    <div class="detail-row">
      <div class="label">{{ translations.region }}</div>
      <div class="value">{{ data.address.region | emptyValue }}</div>
    </div>
    <div class="detail-row">
      <div class="label">{{ translations.country }}</div>
      <div class="value">
        <app-flag [country]="data.address.country" />
        {{ data.address.country | emptyValue }}
      </div>
    </div>

    <ng-container
      *ngTemplateOutlet="customFieldTemplate; context: { customFields: data.customFields }"
    ></ng-container>
  </div>
</ng-template>

<ng-template #organisationTemplate let-data="data">
  <div class="details-grid">
    <div class="detail-row">
      <div i18n="@@shared.title" class="label">Title</div>
      <div class="value">{{ data.name | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.organisation }}</div>
      <div class="value">{{ data.name | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.website }}</div>
      <div class="value">{{ data.website | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.address }}</div>
      <div class="value">{{ data.address.street | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.region }}</div>
      <div class="value">{{ data.address.region | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.zipCode }}</div>
      <div class="value">{{ data.address.zipCode | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div class="label">{{ translations.country }}</div>
      <div class="value">
        <app-flag [country]="data.address.country" />
        {{ getCountryName(data.address.country) | emptyValue }}
      </div>
    </div>

    <ng-container
      *ngTemplateOutlet="customFieldTemplate; context: { customFields: data.customFields }"
    ></ng-container>
  </div>
</ng-template>

<ng-template #genericDetailsTemplate let-data="data" let-label="label">
  <div class="details-grid">
    <div class="detail-row">
      <div class="label">{{ label }}</div>
      <div class="value">{{ data.name | emptyValue }}</div>
    </div>

    <ng-container
      *ngTemplateOutlet="customFieldTemplate; context: { customFields: data.customFields }"
    ></ng-container>
  </div>
</ng-template>

<ng-template #countryTemplate let-data="data">
  <div class="details-grid">
    <div class="detail-row">
      <div class="label">{{ translations.country }}</div>
      <div class="value">
        <app-flag [country]="data.code" />
        {{ data.name | emptyValue }}
      </div>
    </div>
  </div>
</ng-template>

<ng-template #deliveryTemplate let-data="data">
  <div class="details-grid">
    <div class="detail-row">
      <div i18n="@@shared.id" class="label">ID</div>
      <div class="value">{{ data.deliveryId }}</div>
    </div>

    <div class="detail-row">
      <div i18n="@@shared.from" class="label">From</div>
      <div class="value">{{ data.from.name | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div i18n="@@shared.to" class="label">To</div>
      <div class="value">{{ data.to.name | emptyValue }}</div>
    </div>

    <div class="detail-row">
      <div i18n="@@shared.sent" class="label">Sent date</div>
      <div class="value">{{ data.sent | appDate: dateFormat() }}</div>
    </div>

    <div class="detail-row">
      <div i18n="@@shared.delivereddate" class="label">Delivered date</div>
      <div class="value">{{ data.delivered | appDate: dateFormat() }}</div>
    </div>

    <div class="detail-row">
      <div i18n="@@edit-delivery-main-details.traders" class="label">Trader(s)</div>
      <div class="value">
        @for (agent of data.agents; track agent.id) {
          <div>{{ agent.name | emptyValue }}</div>
        }
      </div>
    </div>

    <ng-container
      *ngTemplateOutlet="customFieldTemplate; context: { customFields: data.customFields }"
    ></ng-container>
  </div>
</ng-template>

<ng-template #customFieldTemplate let-customFields="customFields">
  @for (customField of customFields; track customField) {
    <div class="detail-row">
      <div class="label">{{ customField.definition.label }}</div>
      <div class="value">{{ customField.value | emptyValue }}</div>
    </div>
  }
</ng-template>
